import type { FC } from 'react';

import type { ContactMessage, ContactMessageStatus } from '@/types/contactMessage.types';

const STATUS_STYLES: Record<ContactMessageStatus, string> = {
  new: 'bg-brand-50 text-brand-600',
  read: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]',
  archived: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)] line-through',
};

interface ContactMessageCardProps {
  message: ContactMessage;
  isMutating: boolean;
  onMarkRead: (message: ContactMessage) => void;
  onArchive: (message: ContactMessage) => void;
}

const ContactMessageCard: FC<ContactMessageCardProps> = ({
  message,
  isMutating,
  onMarkRead,
  onArchive,
}) => (
  <div className="flex flex-col gap-3 rounded-xl border border-[var(--color-divider)] bg-white p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-[13px] font-bold text-[var(--color-ink)]">
          {message.author.name}
        </p>
        <p className="truncate text-[11px] text-[var(--color-ink-muted)]">
          {message.author.email ?? '—'}
          {message.campusName ? ` · ${message.campusName}` : ''}
        </p>
      </div>
      <span
        className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[message.status]}`}
      >
        {message.status}
      </span>
    </div>

    <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-[var(--color-ink)]">
      {message.message}
    </p>

    <div className="flex items-center justify-between">
      <span className="text-[11px] text-[var(--color-ink-muted)]">
        {new Date(message.createdAt).toLocaleString()}
      </span>
      <div className="flex gap-2">
        {message.status !== 'read' ? (
          <button
            type="button"
            disabled={isMutating}
            onClick={() => onMarkRead(message)}
            className="rounded-lg border border-[var(--color-divider)] bg-white px-3 py-1.5 text-[12px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:border-brand-300 hover:text-brand-600 disabled:opacity-50"
          >
            Mark read
          </button>
        ) : null}
        {message.status !== 'archived' ? (
          <button
            type="button"
            disabled={isMutating}
            onClick={() => onArchive(message)}
            className="rounded-lg border border-[var(--color-divider)] bg-white px-3 py-1.5 text-[12px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:border-brand-300 hover:text-brand-600 disabled:opacity-50"
          >
            Archive
          </button>
        ) : null}
      </div>
    </div>
  </div>
);

export default ContactMessageCard;
